/**
 * Canvas zoom / pan state for the mask editor.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-4 / AUD-2 RC-4) with no change to a single body. The
 * maths already lived in `editor/zoom.ts`; what was still in the component was the STATE around it
 * — `zoom`, `center`, the derived viewBox and handle sizes, `zoomBy`, `resetZoom`, and ED-15's
 * non-passive wheel listener (which is why `svgRef` travels with them: the listener attaches to
 * the SVG's stage element).
 *
 * The `{ passive: false }` on that listener is load-bearing and AUD-2 lists it as healthy — a
 * passive listener silently restores the "page scrolls out from under the canvas" bug ED-15 fixed.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Point } from "@genz/engine-bind";
import { viewBoxFor, screenToNorm, clampZoom, centerForZoomAt, type ViewBox } from "./zoom.js";

const HANDLE_R = 9;
/** Extra padding (viewBox units at zoom 1) added around a handle's visible dot to form
 *  an invisible, larger pointer hit-target — so a corner is easy to grab without the
 *  visible dot itself looking oversized. */
const HANDLE_HIT_PAD = 9;

export interface EditorZoom {
  /** Current zoom factor, 1..MAX_ZOOM. */
  zoom: number;
  setZoom: (z: number) => void;
  /** The normalized image point the view is centered on. */
  center: Point;
  setCenter: (c: Point) => void;
  /** The SVG viewBox this zoom/center implies at the current image size. */
  vb: ViewBox;
  /** Handle radius in viewBox units (constant on-screen size). */
  hr: number;
  /** Pointer hit-target radius in viewBox units. */
  hitr: number;
  /** Stroke width in viewBox units. */
  sw: number;
  /** Zoom by a factor, optionally toward a focal image point (the +/- buttons). */
  zoomBy: (factor: number, focal?: Point) => void;
  resetZoom: () => void;
  /** The canvas `<svg>`; ED-15's wheel listener attaches to its parent stage. */
  svgRef: React.RefObject<SVGSVGElement | null>;
}

export function useEditorZoom({ width: w, height: h }: { width: number; height: number }): EditorZoom {
  const svgRef = useRef<SVGSVGElement>(null);
  // #3 — Canvas zoom (1..MAX_ZOOM) + the normalized point the view is centered on, so the
  // 4 corners can be placed precisely. The SVG viewBox is this window; handles scale by
  // 1/zoom to keep a constant on-screen size.
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState<Point>({ x: 0.5, y: 0.5 });
  /**
   * MN-11 / review-1 M-3 — memoized so its IDENTITY means "the window actually moved".
   *
   * ED-15's wheel effect below has to list `vb` (it reads the whole object through
   * `screenToNorm`); spelling out `vb.x/y/w/h` was semantically complete but not what the rule can
   * see. A fresh object every render would then re-attach the listener on every render, so the
   * honest dep list and the cheap listener only coexist if `vb` is stable — which it now is.
   */
  const vb = useMemo(() => viewBoxFor(w, h, zoom, center), [w, h, zoom, center]);
  /** Handle radius / stroke width in viewBox units — divided by zoom so they keep a
   *  constant on-screen size as the user zooms in. */
  const hr = HANDLE_R / zoom;
  /** Radius of the invisible pointer hit-target around each handle (larger than the
   *  visible dot so corners are easy to grab). */
  const hitr = (HANDLE_R + HANDLE_HIT_PAD) / zoom;
  const sw = 2 / zoom;
  const resetZoom = () => { setZoom(1); setCenter({ x: 0.5, y: 0.5 }); };
  /** Zoom by a factor, optionally toward a focal image point (the +/- buttons). */
  const zoomBy = (factor: number, focal?: Point) => {
    setZoom((z) => {
      const nz = clampZoom(z * factor);
      if (nz <= 1) setCenter({ x: 0.5, y: 0.5 });
      else if (focal) setCenter(focal);
      return nz;
    });
  };
  // ED-15 — WHEEL zoom, as a NON-PASSIVE native listener.
  //
  // Two bugs in one: React registers `wheel` passively at its root container, so the old
  // `onWheel`'s `preventDefault()` never fired and the PAGE scrolled out from under the canvas
  // mid-adjustment; and it re-CENTERED on the cursor point instead of zooming toward it, so every
  // notch teleported the corner being aimed at into the middle of the canvas. Attaching directly
  // with `{ passive: false }` fixes the first; `centerForZoomAt` fixes the second. Re-attached
  // whenever the geometry it reads changes (cheap — one listener).
  useEffect(() => {
    const svg = svgRef.current;
    // The STAGE (svg + its checkerboard margin), so a wheel anywhere over the canvas area
    // zooms — over the margin it used to fall through and scroll the page.
    const el = svg?.parentElement ?? svg;
    if (!el || !svg || typeof el.addEventListener !== "function") return;
    const onWheelNative = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault(); // keep the page still while the operator zooms the canvas
      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const fx = (e.clientX - rect.left) / rect.width;
      const fy = (e.clientY - rect.top) / rect.height;
      const focal = screenToNorm(e.clientX, e.clientY, rect, vb, w, h);
      const nz = clampZoom(zoom * (e.deltaY < 0 ? 1.25 : 1 / 1.25));
      setZoom(nz);
      setCenter(centerForZoomAt(nz, focal, fx, fy));
    };
    // Cast: `el` is HTMLElement|SVGSVGElement, whose union collapses to the base
    // addEventListener overload that only knows `Event`.
    el.addEventListener("wheel", onWheelNative as EventListener, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative as EventListener);
  }, [zoom, vb, w, h, setZoom, setCenter, svgRef]);

  return { zoom, setZoom, center, setCenter, vb, hr, hitr, sw, zoomBy, resetZoom, svgRef };
}
